import type { SignageDeviceFleetStatsApiData, SignageDeviceListApiData } from '@navio/nsdk';
import {
  type BooleanControl,
  type CheckboxControl,
  type FilterSpec,
  filterUnitFor,
  type SharedQuery,
  useFilterUnits
} from 'noddi-ui-web';

import type { SignageContentAssignValues } from './signageContentAssign';
import { SIGNAGE_CONTENT_TYPE_OPTIONS } from './signageContentAssign';
import { resolveSignageFleetActiveQuickFilter, type SignageFleetQuickFilter } from './signageFleetQuickFilter';
import { ONLINE_STATUS_LABEL, SIGNAGE_ONLINE_STATUSES } from './signageOnlineStatus';

// The device list and the stats bar read the same input.
type SignageFleetFilterInput = Omit<
  SharedQuery<NonNullable<SignageDeviceListApiData['query']>, NonNullable<SignageDeviceFleetStatsApiData['query']>>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const signageFleetFilterUnit = filterUnitFor<SignageFleetFilterInput>();

type SignageContentTypeFilter = SignageContentAssignValues['contentType'];

const ONLINE_STATUS_OPTIONS = SIGNAGE_ONLINE_STATUSES.map((value) => ({
  value,
  label: ONLINE_STATUS_LABEL[value]
}));

/** Short labels for filter chips; the assign form uses the longer field labels. */
const CONTENT_TYPE_FILTER_LABEL: Record<SignageContentTypeFilter, string> = {
  none: 'None',
  url: 'URL',
  dashboard: 'Dashboard',
  playlist: 'Playlist'
};

const CONTENT_TYPE_OPTIONS = SIGNAGE_CONTENT_TYPE_OPTIONS.map(({ value }) => ({
  value,
  label: CONTENT_TYPE_FILTER_LABEL[value]
}));

const isPaired = signageFleetFilterUnit.boolean('isPaired', {
  title: 'Paired',
  description: 'Filter by whether the device is currently paired.'
});
const onlineStatuses = signageFleetFilterUnit.multiSelect('onlineStatuses', {
  title: 'Online status',
  options: ONLINE_STATUS_OPTIONS,
  exclude: true
});
const contentTypes = signageFleetFilterUnit.multiSelect('contentTypes', {
  title: 'Content type',
  options: CONTENT_TYPE_OPTIONS,
  exclude: true
});
const hasPendingCommands = signageFleetFilterUnit.boolean('hasPendingCommands', {
  title: 'Queued commands',
  description: 'Devices with at least one pull-command waiting for delivery.'
});
const noContent = signageFleetFilterUnit.boolean('noContent', {
  title: 'No content',
  description: 'Devices with no playlist, dashboard, preset, or URL assigned.'
});
const needsAttention = signageFleetFilterUnit.boolean('needsAttention', {
  title: 'Needs attention',
  description: 'Offline, never seen, degraded, unpaired, queued commands, or no content.'
});

/** Every fleet filter a chip replaces. */
const FLEET_SPECS: FilterSpec<object>[] = [
  isPaired,
  onlineStatuses,
  contentTypes,
  hasPendingCommands,
  noContent,
  needsAttention
];

/** Chips that narrow to one flag; the online status chips and `unpaired` write their own filter. */
const QUICK_FILTER_FLAGS: Partial<
  Record<SignageFleetQuickFilter, FilterSpec<object, BooleanControl | CheckboxControl>>
> = { needs_attention: needsAttention, pending: hasPendingCommands, no_content: noContent };

/** Fleet list filters shared by partner and admin (query-param backed), plus the stats bar chips over them. */
export const useSignageFleetFilters = ({
  serviceDepartmentIds
}: {
  /** Page scope (partner's selected departments); the chips and controls never write it. */
  serviceDepartmentIds?: number[];
} = {}) => {
  const filters = useFilterUnits([
    signageFleetFilterUnit.locked({ serviceDepartmentIds }),
    isPaired,
    onlineStatuses,
    contentTypes,
    hasPendingCommands,
    noContent,
    needsAttention
  ]);

  const activeQuickFilter = resolveSignageFleetActiveQuickFilter({
    needsAttention: filters.apiInput.needsAttention,
    hasPending: filters.apiInput.hasPendingCommands,
    noContent: filters.apiInput.noContent,
    isPaired: filters.apiInput.isPaired,
    onlineStatusValues: filters.apiInput.onlineStatuses ?? []
  });

  /** One chip, one scope: every fleet filter is cleared, so no earlier filter is left behind. */
  const applyQuickFilter = (key: SignageFleetQuickFilter | null) => {
    FLEET_SPECS.forEach((spec) => filters.controlOf(spec)?.reset());
    const onlineStatus = SIGNAGE_ONLINE_STATUSES.find((status) => status === key);
    if (onlineStatus) {
      filters.controlOf(onlineStatuses)?.onChange({ include: [onlineStatus], exclude: [] });
    }
    if (key === 'unpaired') {
      filters.controlOf(isPaired)?.onChange(false);
    }
    const flag = key && QUICK_FILTER_FLAGS[key];
    if (flag) {
      filters.controlOf(flag)?.onChange(true);
    }
  };

  return {
    filters,
    activeQuickFilter,
    applyQuickFilter
  };
};
